/**
 * FYKA Navigation Stability
 * -------------------------
 * Keeps the current document visually frozen from the moment an internal
 * header/hero link is activated until Safari/Shopify commits the next page.
 *
 * Why this exists:
 * On iOS Safari, a tapped link inside an absolutely-positioned mobile menu can
 * cause the current document to re-focus/re-scroll while the next document is
 * loading. That produces the visible upward "jump" before the URL changes.
 *
 * This script does NOT animate, morph or replace page content. It only prevents
 * the old page from moving during the short navigation gap.
 */
(() => {
  'use strict';

  const NAV_ROOT_SELECTOR = '#header-group';
  const LEAVING_CLASS = 'fyka-navigation-leaving';
  const COARSE_POINTER = window.matchMedia?.('(pointer: coarse)');

  let freezeState = null;
  let pointerStart = null;

  function getEligibleAnchor(target, event) {
    const element = target instanceof Element ? target : target?.parentElement;
    const anchor = element?.closest?.(`${NAV_ROOT_SELECTOR} a[href]`);

    if (!anchor) return null;
    if (anchor.hasAttribute('download')) return null;
    if (anchor.target && anchor.target !== '_self') return null;

    if (event) {
      if ('button' in event && event.button !== 0) return null;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
    }

    const rawHref = anchor.getAttribute('href') || '';
    if (!rawHref || rawHref.startsWith('#')) return null;
    if (/^(mailto:|tel:|sms:|javascript:)/i.test(rawHref)) return null;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (_) {
      return null;
    }

    if (url.origin !== window.location.origin) return null;

    // Same document + different hash: keep native anchor scrolling.
    if (
      url.pathname === window.location.pathname &&
      url.search === window.location.search &&
      url.hash
    ) {
      return null;
    }

    // Exact same URL: no navigation lock is needed.
    if (url.href === window.location.href) return null;

    return { anchor, url };
  }

  function freezeCurrentViewport() {
    if (freezeState || !document.body) return;

    const html = document.documentElement;
    const body = document.body;
    const x = window.scrollX || window.pageXOffset || 0;
    const y = window.scrollY || window.pageYOffset || 0;

    freezeState = {
      x,
      y,
      htmlScrollBehavior: html.style.scrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      bodyTouchAction: body.style.touchAction,
    };

    // Disable the global `scroll-behavior: smooth` before locking. This matters
    // on Safari because focus/navigation scrolls can otherwise animate visibly.
    html.style.scrollBehavior = 'auto';
    html.classList.add(LEAVING_CLASS);

    // Standard scroll-lock technique: preserve the exact pixels currently on
    // screen while making the old document impossible to scroll/reflow away.
    body.style.position = 'fixed';
    body.style.top = `${-y}px`;
    body.style.left = `${-x}px`;
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
  }

  function restoreCurrentViewport() {
    if (!freezeState || !document.body) return;

    const state = freezeState;
    freezeState = null;

    const html = document.documentElement;
    const body = document.body;

    body.style.position = state.bodyPosition;
    body.style.top = state.bodyTop;
    body.style.left = state.bodyLeft;
    body.style.right = state.bodyRight;
    body.style.width = state.bodyWidth;
    body.style.overflow = state.bodyOverflow;
    body.style.touchAction = state.bodyTouchAction;

    html.classList.remove(LEAVING_CLASS);
    html.style.scrollBehavior = 'auto';

    // BFCache can restore the old DOM including its inline lock styles. Put the
    // user back at the exact previous scroll position without animation.
    window.scrollTo(state.x, state.y);

    requestAnimationFrame(() => {
      html.style.scrollBehavior = state.htmlScrollBehavior;
    });
  }

  // On touch devices freeze on a genuine tap *before* the click default action.
  // This prevents Safari from scrolling the focused menu link into view during
  // the network gap. Drag/scroll gestures are ignored by the movement threshold.
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!COARSE_POINTER?.matches) return;

      const eligible = getEligibleAnchor(event.target, event);
      if (!eligible) {
        pointerStart = null;
        return;
      }

      pointerStart = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        anchor: eligible.anchor,
      };
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      if (!COARSE_POINTER?.matches || !pointerStart) return;
      if (event.pointerId !== pointerStart.pointerId) return;

      const dx = event.clientX - pointerStart.x;
      const dy = event.clientY - pointerStart.y;
      const moved = Math.hypot(dx, dy);
      const eligible = getEligibleAnchor(event.target, event);

      if (
        moved <= 12 &&
        eligible &&
        eligible.anchor === pointerStart.anchor
      ) {
        freezeCurrentViewport();
      }

      pointerStart = null;
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'pointercancel',
    () => {
      pointerStart = null;
    },
    { capture: true, passive: true }
  );

  // Intercept only header/hero internal navigations. Navigation remains a normal
  // full Shopify document request; we merely control when it starts so the old
  // viewport stays perfectly static until the browser commits the new page.
  document.addEventListener(
    'click',
    (event) => {
      const eligible = getEligibleAnchor(event.target, event);
      if (!eligible) return;

      event.preventDefault();
      freezeCurrentViewport();

      // Give the frozen state one paint opportunity. The user sees the exact
      // same frame, not a close-menu animation or a scroll-to-top.
      requestAnimationFrame(() => {
        window.location.assign(eligible.url.href);
      });
    },
    true
  );

  // If Safari restores this page from BFCache, undo the temporary lock.
  window.addEventListener('pageshow', restoreCurrentViewport, { passive: true });
})();
